import React, { useState, useRef } from 'react';
import { ResizableBox } from 'react-resizable';
import classnames from 'classnames';

import { useConstantRefCallback, useWindowBinding } from '@xmatters/react';
import { makeReactResizableHandleGetter } from 'components/ResizePanel';
import { MAX_HEIGHT, MIN_HEIGHT, MAX_WIDTH, MIN_WIDTH } from '../constants';
import { clampGeometryToViewport, storeHelpGeometry } from '../utils';
import { useHelpGeometryContext } from '../hooks';
import styles from '../HelpView.module.scss';

const minConstraints = [MIN_WIDTH, MIN_HEIGHT];
const maxConstraints = [MAX_WIDTH, MAX_HEIGHT];
const resizeHandles = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'];

const buildHandle = makeReactResizableHandleGetter({
  classes: {
    handle: styles.handle,
    handleHighlight: styles.handleHighlight,
  },
});

export default function HelpViewResizers() {
  const { geometry, geometryRef, setGeometry } = useHelpGeometryContext();
  const [currentResize, setCurrentResize] = useState(null);
  const isResizing = !!currentResize;
  const currentResizeRef = useRef();
  currentResizeRef.current = currentResize;

  const onResize = useConstantRefCallback((event, resizeDetails) => {
    // Prevent scrolling unrelated background elements when resizing:
    event.preventDefault();
    const { handle, size } = resizeDetails;
    const { startingGeometry, startingSize } = currentResizeRef.current;
    const newGeometry = { ...geometryRef.current };

    const deltaWidth = size.width - startingSize.width;
    const deltaHeight = size.height - startingSize.height;

    newGeometry.height = startingGeometry.height + deltaHeight;
    newGeometry.width = startingGeometry.width + deltaWidth;

    if (handle[0] === 'n') {
      newGeometry.y = startingGeometry.y - deltaHeight;
    }
    if (handle[0] === 'w' || handle[1] === 'w') {
      newGeometry.x = startingGeometry.x - deltaWidth;
    }
    setGeometry(newGeometry);
  });

  useWindowBinding(
    'resize',
    () => {
      setGeometry(clampGeometryToViewport(geometryRef.current));
    },
    [setGeometry, geometryRef],
  );

  return (
    <ResizableBox
      height={geometry.height}
      width={geometry.width}
      minConstraints={minConstraints}
      maxConstraints={maxConstraints}
      axis="both"
      resizeHandles={resizeHandles}
      handle={buildHandle}
      onResize={onResize}
      onResizeStart={(event, resizeDetails) => {
        setCurrentResize({
          startingGeometry: geometry,
          startingSize: resizeDetails.size,
        });
      }}
      onResizeStop={() => {
        setGeometry(clampGeometryToViewport(geometry));
        storeHelpGeometry(geometry);
        setCurrentResize(null);
      }}
      className={classnames(styles.resize, {
        [styles.isResizing]: isResizing,
      })}
    />
  );
}
